import type React from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { HiX } from "react-icons/hi";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  style?: "danger" | "default";
  size?: "sm" | "md" | "lg" | "xl";
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
}

const sizeClasses = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  style = "default",
  size = "md",
  closeOnOverlayClick = true,
  showCloseButton = true,
}: ModalProps) {
  // A click only counts when the press STARTED on the overlay too. Releasing the button over the
  // overlay after pressing inside the dialog — finishing a text selection, dragging a slider past
  // the edge — still fires a click whose target is the overlay, and closing on that throws away
  // whatever the user was in the middle of doing.
  const pressStartedOnOverlay = useRef(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    pressStartedOnOverlay.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    const startedOnOverlay = pressStartedOnOverlay.current;
    pressStartedOnOverlay.current = false;

    if (closeOnOverlayClick && startedOnOverlay && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (
      closeOnOverlayClick &&
      e.target === e.currentTarget &&
      (e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      onClose();
    }
  };

  // Portal to document.body so the fixed overlay is never trapped by an
  // ancestor's transform/filter (e.g. card hover effects), which would
  // otherwise collapse it into the card instead of centering on the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black bg-opacity-50"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={`bg-surface rounded-lg shadow-xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-800`}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center px-14 py-4 border-b border-gray-200 dark:border-gray-800">
          <h2
            id="modal-title"
            className={`h4 text-center ${style === "danger" ? "text-error-500" : ""}`}
          >
            {title}
          </h2>
          {showCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-secondary-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full p-1"
              aria-label="Close modal"
            >
              <HiX className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>,
    document.body
  );
}
